/**
 * Lauft Agent Portal Documentation Data
 * 
 * Structure:
 * - Categories contain articles
 * - Each article has content in markdown-like format
 */

export const docsCategories = [
  {
    slug: 'getting-started',
    title: 'Getting Started',
    description: 'New to Lauft? Start here.',
    icon: 'PlayCircle',
    color: '#6366f1',
    articles: [
      {
        slug: 'welcome-to-agent-portal',
        title: 'Welcome to the Agent Portal',
        summary: 'Your command center for working with users, understanding their preferences, and turning intent into closed deals.',
        content: `
# Welcome to the Agent Portal

The Lauft Agent Portal is your command center for working with users, understanding their preferences, and turning intent into closed deals. Built specifically for mobile-first home search behavior, the portal gives you visibility into what users are liking, disliking, and requesting so you can act faster and serve them better.

Whether you are nurturing one active user or scaling relationships across dozens of users, the Agent Portal keeps everything organized in one place so you can focus on what matters most: helping clients find the right home and moving transactions forward.

## What You Can Do

Inside the portal, you have access to a full suite of tools designed to streamline communication, discovery, and deal coordination.

### Work With Claimed Users

Once you claim a user, they become part of your active pipeline. From their profile you can:

- Send messages directly through the platform
- Share listings tailored to their preferences
- Review their recent activity in the app
- See what types of homes they are engaging with most
- Track questions, showing requests, and listing interactions

This insight helps you tailor outreach and prioritize the most motivated users.

### Claim New Users

Browse the pool of available users in your market and claim new opportunities as they become active. Claiming a user allows you to:

- Begin messaging them immediately
- Respond to listing inquiries
- Schedule showings
- Start building a relationship from the moment they show intent

Claiming early increases your chances of becoming the agent who guides them through the full buying process.

### Schedule Showings

Coordinate property tours directly from the portal. You can:

- View upcoming showing requests
- Confirm dates and times with users
- Track scheduled tours in one centralized view
- Stay organized across multiple clients

This eliminates scattered emails and texts and keeps your schedule aligned with user demand.

### Manage Properties

Add and manage listings that you want to share with users. This includes:

- Uploading new properties
- Updating details and photos
- Recommending specific homes to individual users
- Maintaining a curated set of listings for outreach

Sharing properties through the portal ensures users receive them directly inside the Lauft app.

## Getting Around the Portal

Navigation is designed to be simple and fast. The sidebar on the left gives you quick access to every major feature:

- **Dashboard** - Your high-level overview of activity and performance. Monitor metrics, recent interactions, and user engagement at a glance.
- **Additional Listings** - Browse available listings and manage the properties you want to surface to users.
- **Add Property** - Create a new listing to share with users by entering property details and uploading photos.
- **Scheduled Showings** - View all upcoming tours and requests in one place so you never miss an appointment.
- **User Management** - Manage users you have already claimed. Review their profiles, activity, and conversations.
- **Available Users** - Browse users who are currently unclaimed and actively searching in your area.
- **Conversations** - Send and receive messages with users directly through the platform to keep communication centralized and documented.
- **Help Center** - Access guides, tutorials, and support resources whenever you need assistance.

## Best Practices for New Agents

To get the most value out of the portal, we recommend:

- Completing your profile so users immediately recognize and trust you
- Checking the Dashboard daily for new activity
- Responding quickly to messages and showing requests
- Regularly reviewing user preferences and engagement patterns
- Claiming new users as soon as they appear in your market

Fast, informed responses consistently lead to better outcomes for both agents and users.

## Next Steps

Start by visiting [Setting Up Your Profile](/docs/getting-started/setting-up-your-profile) to make a strong first impression on users and ensure your contact information and photo are complete.

From there, explore each section of the portal to familiarize yourself with the tools available. The more comfortable you are navigating the platform, the faster you will be able to connect with users and close deals.

If you have questions at any point, head to the Help Center in the sidebar for step-by-step guides or reach out to our support team for assistance.
        `,
        tags: ['onboarding', 'basics', 'dashboard'],
        readTime: 5,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'setting-up-your-profile',
        title: 'Setting Up Your Profile',
        summary: 'Your agent profile is what users see when you claim them. A complete, professional profile builds credibility and increases response rates.',
        content: `
# Setting Up Your Profile

Your agent profile is what users see when you claim them and when they interact with you inside the Lauft app. A complete, professional profile builds credibility, increases response rates, and helps users feel confident working with you from the very first interaction.

Taking a few minutes to set up your profile properly can make a meaningful difference in how users engage with you on the platform.

## Where to Find Your Profile

Your profile settings are located directly on the **Dashboard** inside the Agent Portal.

From here, you can:

- Upload or update your photo
- Review your contact details
- Request changes to key information
- Manage the email address visible to users

Any updates you make help ensure users always see accurate and up-to-date information.

## Uploading Your Photo

Your photo is one of the first things users notice. A clear, friendly headshot helps create trust and signals professionalism.

### How to Upload or Change Your Photo

1. Go to the Dashboard
2. Click on the photo area or upload button
3. Select a professional headshot from your computer
4. Save your changes

### Tips for a Great Photo

- Use a high-quality, professional headshot
- Choose good lighting and a neutral background
- Dress professionally
- Smile and appear approachable
- Avoid group photos, logos, or casual selfies

## Your Contact Information

From the Dashboard, you can review and request updates to the following information:

- **Phone Number** - How users can reach you
- **Email** - Your primary contact email
- **Brokerage** - Your affiliated brokerage name
- **License Number** - Your real estate license number

If any of this information needs to be updated, you can submit a change request directly from the Dashboard. Our team will review and apply updates to keep everything accurate across the platform.

## Alias Email for User Communication

You also have the option to add an alias email address to your profile.

An alias email is the address that users will see when they receive messages or introduction emails from you through Lauft. This can be helpful if you prefer to present a branded or team-based email address publicly.

**Important things to know:**

- The alias email is only what users see
- All account notifications, billing messages, and platform communications will still go to your primary account email
- Replies from users are routed correctly to you
- Updating your alias does not affect your login credentials

This allows you to maintain a professional outward-facing email while keeping operational communications separate and secure.

## Where Your Profile Appears

Your profile is shown to users in several important places across the platform:

- **When you claim a user** - Users see your photo, name, brokerage, and contact details inside the app.
- **Via email introductions** - Users receive an automated email introducing you when you claim them.
- **In conversations** - Your photo and profile information appear when messaging users through the platform.

Because your profile appears in multiple high-visibility locations, keeping it complete and polished is strongly recommended.

## Best Practices

To make the most of your profile:

- Upload your photo before claiming users
- Double-check your phone number and email
- Add an alias email if you want a different public-facing address
- Keep your brokerage and license information current
- Update your profile any time your details change

## Next Steps

Once your profile is complete, head back to the Dashboard and start exploring available users or claiming your first connection.

If you need help making updates, visit the Help Center from the sidebar or reach out to our support team for assistance.
        `,
        tags: ['profile', 'photo', 'setup', 'dashboard', 'email'],
        readTime: 4,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'understanding-your-dashboard',
        title: 'Understanding Your Dashboard',
        summary: 'Your Dashboard provides a real-time snapshot of user activity, your current workload, and quick access to the actions that matter most.',
        content: `
# Understanding Your Dashboard

Your Dashboard is the first screen you see when you log into the Lauft Agent Portal. It provides a real-time snapshot of user activity in your market, your current workload, and quick access to the actions that matter most.

From here, you can monitor performance, identify new opportunities, and stay on top of conversations and showings without jumping between tools.

## Header Overview

At the top of the dashboard, you will see:

- A greeting and today's date
- Your profile photo
- Your current Points balance, along with an option to add more points
- A Get Support button for quick access to help

This area keeps key account-level information visible at all times.

## Key Metrics

Across the top of the dashboard are four primary metric cards. These give you an instant sense of activity in your market and within your account.

### Available Nearby

Shows how many active users in your area are currently unclaimed and available for you to work with.

This is your opportunity pool and a great place to look daily for new users to claim.

### Users Claimed

Displays the number of users you are currently working with.

These are active relationships inside your pipeline.

### Matches Sent

Tracks how many properties you have shared with users through the platform.

Sending matches regularly keeps users engaged and increases momentum toward scheduling showings.

### Invited Users

Shows how many people you have personally invited to join Lauft.

Inviting users is a way to expand your local pool and generate new opportunities.

## Newest Users Table

The Newest Users section lists recent sign-ups in your market along with helpful qualification details.

Each row includes:

- User name
- Tier level (Bronze, Silver, Gold, Platinum)
- Credit score (when available)
- Monthly income (when available)

This table helps you quickly assess which users may be ready for outreach or claiming.

Some users may not yet have full financial details completed. In those cases, values will appear as "N/A" until the user finishes their profile.

## Quick Actions Panel

On the right side of the dashboard is the Quick Actions panel.

These shortcuts allow you to move fast without navigating through multiple screens:

- **Invite New User** - Send an invitation for someone to join Lauft
- **View Assigned Showings** - Jump directly to your upcoming tours and requests

Use this panel when you want to take immediate action from your home screen.

## Agent Information Card

Below Quick Actions is your Agent Information section.

This displays:

- Your name
- Your public-facing email
- Your phone number

This is the same information users see when you claim them and communicate through the app, so it is a good idea to periodically review it for accuracy.

## Sidebar Navigation

Along the left side of the screen is the main navigation bar, which gives you access to every part of the portal.

Icons in the sidebar link to:

- Dashboard
- Listings
- Add Property
- Scheduled Showings
- User Management
- Conversations
- Help Center
- Logout

Hovering over or selecting an icon will take you to that section.

## How to Use the Dashboard Effectively

We recommend using your dashboard as a daily command center:

- Check Available Nearby for new users to claim
- Review Matches Sent to stay active with your pipeline
- Monitor Invited Users if you are growing your network
- Scan the Newest Users list for high-potential opportunities
- Use Quick Actions to jump into showings or invitations
- Keep an eye on your Points balance

Consistent daily use leads to faster response times and better outcomes with users.

## Customizing Your View

Some dashboard elements can be customized.

Click the settings icon on the dashboard to control which widgets appear on your screen and tailor the layout to your workflow.

This allows you to prioritize the information that matters most to your business.
        `,
        tags: ['dashboard', 'metrics', 'analytics', 'overview'],
        readTime: 5,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'installing-lauft-pro-app',
        title: 'Installing the Lauft Pro App',
        summary: 'Add Lauft Pro to your phone home screen for one-tap access, faster load times, and a full app-like experience.',
        images: {
          'pwa-ios-step1-share': require('../../assets/pwa-install/pwa-ios-step1-share.png'),
          'pwa-ios-step2-add': require('../../assets/pwa-install/pwa-ios-step2-add.png'),
          'pwa-ios-step3-confirm': require('../../assets/pwa-install/pwa-ios-step3-confirm.png'),
          'pwa-installed-icon': require('../../assets/pwa-install/pwa-installed-icon.png'),
          'pwa-dashboard': require('../../assets/pwa-install/pwa-dashboard.png')
        },
        content: `
# Installing the Lauft Pro App

Lauft Pro is the mobile version of the Agent Portal, designed to give you full access to your dashboard, users, messages, and showings from anywhere. Instead of opening a browser every time, you can install Lauft Pro directly to your phone home screen and launch it like any other app.

Once installed, Lauft Pro runs in full-screen mode, loads faster, and gives you a cleaner experience without browser tabs or address bars in the way. This guide walks you through installing it on both iPhone and Android.

## Why Install Lauft Pro

Adding Lauft Pro to your home screen gives you several advantages over using the website in a browser:

- **One-tap access** from your home screen
- **Full-screen experience** with no browser bars eating into your view
- **Faster load times** thanks to cached assets
- **A dedicated app icon** so Lauft Pro is always one swipe away
- **Cleaner navigation** built specifically for mobile

If you are managing users on the go, installing Lauft Pro will quickly become essential to your daily workflow.

## Installing on iPhone

Installing Lauft Pro on iPhone takes about 15 seconds using Safari. Make sure you are using **Safari** specifically, since this feature is built into Apple's browser and does not work the same way in Chrome on iOS.

### Step 1: Open Lauft Pro in Safari

Open Safari on your iPhone and go to [partner.lauft.app](https://partner.lauft.app). When the page loads, you may see a prompt at the bottom of the screen with instructions to install the app. You can either follow that prompt or use the steps below.

Tap the **Share button** at the bottom of Safari (the square icon with an arrow pointing up).

![Tap the Share button in Safari](pwa-ios-step1-share)

### Step 2: Tap "Add to Home Screen"

In the share menu that appears, scroll down until you see **Add to Home Screen**. Tap it.

![Tap Add to Home Screen](pwa-ios-step2-add)

### Step 3: Confirm the Name and Tap Add

A confirmation screen will appear with the Lauft Pro icon and the app name already filled in. You can leave the name as-is or customize it.

Tap **Add** in the top right corner to finish.

![Tap Add to confirm](pwa-ios-step3-confirm)

### You're All Set

Lauft Pro is now installed on your home screen. Look for the Lauft Pro icon and tap it to launch the app any time.

![Lauft Pro installed on your home screen](pwa-installed-icon)

From here, Lauft Pro behaves just like a native app. Once you sign in, your session will stay active so you can jump in and out without having to log in every time.

![The Lauft Pro dashboard](pwa-dashboard)

## Installing on Android

Installing Lauft Pro on Android works similarly, using Chrome.

### Step 1: Open Lauft Pro in Chrome

Open Chrome on your Android device and go to [partner.lauft.app](https://partner.lauft.app). In many cases, Chrome will automatically show an **Install app** banner at the bottom of the screen as soon as the page loads. If you see it, tap **Install** and you are done.

### Step 2: Use the Menu if You Don't See the Banner

If the banner does not appear, you can install Lauft Pro manually:

- Tap the **three-dot menu** in the top right corner of Chrome
- Select **Install app** or **Add to Home screen**
- Confirm by tapping **Install**

### You're All Set

Lauft Pro will be added to your home screen and your app drawer, ready to launch like any other Android app.

## Troubleshooting

If something is not working as expected, here are a few things to check:

- **iPhone:** Make sure you are using Safari, not Chrome or another browser. The Add to Home Screen option only appears in Safari.
- **Android:** If you do not see the Install app option in the Chrome menu, try refreshing the page or clearing your cache.
- **Old version showing:** If you previously bookmarked the site and an outdated version appears, delete the old icon and reinstall using the steps above.
- **Sign-in issues after install:** Sign in once after installing and your session will persist going forward.

If you continue to have trouble installing Lauft Pro, reach out to our support team and we will help you get set up.

## Next Steps

Now that you have Lauft Pro installed, take a moment to explore the mobile experience. The navigation, dashboard, and user management tools all work the same way they do on desktop, optimized for your phone.

A good next step is to head to [Understanding Your Dashboard](/docs/getting-started/understanding-your-dashboard) so you know exactly what every metric on your home screen means.
        `,
        tags: ['pwa', 'mobile', 'install', 'app', 'lauft pro', 'iphone', 'android'],
        readTime: 4,
        updatedAt: '2026-05-11'
      }
    ]
  },
  {
    slug: 'managing-users',
    title: 'Managing Users',
    description: 'Invitations, leads, and buyer relationships.',
    icon: 'Users',
    color: '#8b5cf6',
    articles: [
      {
        slug: 'understanding-invitations',
        title: 'Understanding Invitations',
        summary: 'Bring your existing clients into Lauft and manage the entire relationship in one place with zero points.',
        content: `
# Understanding Invitations

Invitations allow you to bring your existing clients directly into the Lauft platform and manage the entire relationship in one place.

Instead of relying only on users discovered inside the marketplace, invitations are designed for people you already know and are already working with such as referral clients, sphere contacts, past leads you have re-engaged, or active house hunters who are texting and emailing you today.

Once a user accepts your invitation and creates their account, they are exclusively linked to you inside Lauft. No other agents can view them, claim them, or interact with them.

Invited users also cost zero points to connect. There is no claiming process and no competition. They simply become part of your private pipeline inside the platform.

## Why Invite Existing Users

Inviting your current clients into Lauft unlocks the full power of the platform for relationships you already control.

### Centralize Communication

Instead of juggling text threads, emails, spreadsheets, and calendar notes, Lauft gives you one place to:

- Message users
- Send properties
- Track questions
- Manage showing requests
- Review engagement history

Everything is organized and searchable so nothing slips through the cracks.

### Gain Deeper Insight Into Preferences

When users browse homes in the app, swipe on listings, and interact with properties, Lauft captures behavioral data that goes far beyond a typical intake form.

By inviting existing clients, you gain:

- Visibility into what types of homes they like and dislike
- Feedback based on real listing photos
- A clearer picture of their style, layout preferences, and priorities
- Faster alignment on the right homes to tour

This allows you to stop guessing and start sending highly targeted recommendations.

### Protect Your Client Relationship

Invited users are locked to your account the moment they join.

That means:

- No other agents can see them
- No one else can message them
- They cannot be claimed by someone else
- They remain fully yours to serve

For agents, this creates confidence that your existing relationships stay protected inside the platform.

### Save Points and Move Faster

Because invited users automatically connect to you, they:

- Cost 0 points to onboard
- Do not appear in the available user pool
- Skip the claiming process entirely

This lets you reserve points for new opportunities while still bringing your active clients into the system instantly.

### Deliver a More Modern Experience

Inviting users into Lauft also gives your clients access to a better way to search.

They can:

- Swipe through listings on their phone
- Request showings directly in-app
- Ask questions on properties
- Compare homes more easily
- Stay engaged between tours

This elevates your service level and differentiates you from agents who still rely only on portals and email chains.

## How Invitations Work

The invitation process is simple and designed to be frictionless for both you and the user:

1. You send an invitation from the Agent Portal
2. The user receives an email or text invitation
3. They create their Lauft account using that link
4. They are instantly and permanently linked to you
5. No other agents can view or interact with them
6. The user costs zero points to connect
7. A conversation thread is created automatically

From that moment forward, the user becomes part of your private client roster.

## What Happens After a User Joins

Once a user accepts your invitation:

- They appear in your User Management section
- You can message them immediately
- Send them listings
- Schedule showings
- Track their activity and preferences
- See their tier and onboarding status
- Review questions and tour requests

Everything flows through the same tools you already use for marketplace users.

## Best Practices for Inviting Users

To get the most out of invitations:

- Invite users early in the relationship
- Explain why you are sending the invite
- Let them know the app connects them only to you
- Encourage them to complete their profile
- Follow up if they have not signed up yet
- Walk them through the download on a call if needed

Agents who consistently invite their own clients tend to build stronger pipelines, respond faster, and convert more efficiently.

## When to Use Invitations vs Claiming Users

**Use invitations when:**

- You already represent the user
- They were referred to you
- They are actively house hunting with you
- You want to bring an offline relationship into Lauft

**Use claiming when:**

- You discover new users inside the marketplace
- You are competing for unclaimed opportunities
- You are expanding your pipeline in new areas

Both tools are powerful. Invitations simply give you a way to bring your existing business into the platform and operate everything from one dashboard.

## Next Steps

To invite a user, go to the Dashboard and click **Invite New User** in the Quick Actions panel.

Once they accept, they will automatically appear in your User Management section and be ready for you to start sending homes, scheduling tours, and tracking their activity.

If you need help inviting someone or have questions about exclusivity or points, head to the Help Center or reach out to our support team.
        `,
        tags: ['invitations', 'users', 'claiming', 'points'],
        readTime: 6,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'claiming-users',
        title: 'Claiming Users',
        summary: 'Connect with people actively searching for homes and bring them into your personal pipeline.',
        content: `
# Claiming Users

Claiming users allows you to connect with people actively searching for homes inside Lauft and bring them into your personal pipeline.

When you claim a user, they become exclusively linked to your account. You can begin messaging them, send properties, schedule showings, and track their activity inside the app.

Before claiming, you are always able to review key qualification details so you can make informed decisions about where to spend your points.

## How You Are Notified About New Users

When new users become active in your area, you will:

- Receive an email notification
- See updated counts on your Dashboard
- Find them listed in the Available Users screen

This ensures you never miss opportunities as new demand enters your market.

## Where to View Available Users

To browse users who are ready to be claimed:

1. Open the Agent Portal
2. Click **Available Users** in the sidebar
3. Review the list of active users in your market
4. Select any user to view their full profile

From this screen, you can explore each user in detail before deciding whether to claim them.

## What You Can Review Before Claiming

Before spending points, you have full visibility into a user's onboarding data and in-app behavior.

This includes:

- Credit range (when provided)
- Income range (when provided)
- Current living situation
- Full onboarding profile
- Timeline to purchase
- Preferences and must-haves
- Properties they have liked
- Homes they have disliked
- Questions they have asked
- Showing requests they have submitted

This transparency allows you to prioritize higher-intent users and align your outreach with their real preferences.

## How Claiming Works

Once you decide to move forward:

1. Click **Claim User** from their profile
2. Confirm the point cost
3. The points are deducted from your balance
4. The user is notified that you are now their agent
5. A conversation thread is created automatically
6. You can begin working with them immediately

Claiming protects the connection so no other agents can view or interact with that user.

## User Tiers and Point Costs

Each user inside Lauft is assigned a Tier based on their onboarding information. Tiers help signal readiness and determine the number of points required to claim them.

Higher tiers typically reflect stronger financial readiness and more complete profiles, while lower tiers may still be early in their journey.

| Tier | Points | Credit | Income | Current Living |
| --- | --- | --- | --- | --- |
| 🥉 Bronze | 10 | Unknown/Other AND | Unknown/Other | N/A |
| 🥈 Silver | 25 | 620+ OR | 4k+ Individual | N/A |
| 🥇 Gold | 45 | 620+ AND | 4k+ Individual | N/A |
| 💎 Platinum | 65 | 700+ AND | 4k+ AND | Already owns house |

## How to Use Tiers Strategically

Tiers are designed to help you allocate points effectively based on your business goals.

You may want to:

- Focus on Platinum and Gold users for near-term opportunities
- Work Silver users as medium-term prospects
- Nurture Bronze users for longer-term pipelines
- Balance high-intent claims with early-stage relationships

Because you can review each user's full profile before claiming, you are always in control of how you deploy points.

## Best Practices for Claiming Users

To maximize results:

- Check Available Users daily
- Review profiles thoroughly before claiming
- Look at likes and dislikes to understand style preferences
- Prioritize users actively engaging with listings
- Respond quickly once claimed
- Send a welcome message immediately
- Begin sharing homes within the first 24 hours

## Important Reminder About Invitations

Users you invite personally through the Invite New User feature:

- Cost zero points
- Are automatically linked to your account
- Do not appear in the Available Users pool
- Cannot be claimed by other agents

Claiming is used for marketplace users, while invitations are the best way to bring your own clients onto the platform.

## Next Steps

To start claiming users:

1. Visit **Available Users** in the sidebar
2. Review profiles
3. Choose the opportunities that fit your business
4. Claim and begin working with them immediately

If you have questions about tiers, points, or claiming, visit the Help Center or reach out to our support team for assistance.
        `,
        tags: ['claiming', 'users', 'points', 'tiers', 'available users'],
        readTime: 5,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'sending-matches',
        title: 'Sending Matches',
        summary: 'Send properties directly to users through the app and leverage AI-driven recommendations.',
        videoUrl: 'https://drive.google.com/file/d/1Vu116fk0DHYXkazir9Dzpzcumh8_8Pmg/preview',
        images: {
          'curated-matches': require('../../assets/curated-matches.png')
        },
        content: `
# Sending Matches

Sending matches is one of the most powerful ways to engage users inside Lauft.

Matches are properties you send directly to a user through the app. They appear in the user's browsing feed and are presented just like homes they would normally swipe through, keeping the experience natural while letting you guide discovery.

You can send matches from the User Management screen, where you also have access to Lauft's AI-driven recommendations that highlight listings the system expects each user to like next.

## Where to Send Matches

To send matches:

1. Open the Agent Portal
2. Click **User Management** in the sidebar
3. Select the user you want to work with
4. Navigate to their Matches or Recommendations section

From here, you can browse listings, review AI-suggested homes, and send properties directly to that user.

## Using Lauft's AI Recommendations

Inside the User Management view, you will see a set of recommended properties generated by Lauft's algorithm.

These recommendations are based on:

- The user's swipe history
- Likes and dislikes on listing photos
- Style and layout preferences
- Budget and timeline inputs
- Engagement patterns
- Similar users with matching behavior

This allows you to focus your time on listings that already align with what the user is gravitating toward.

## Sending Algorithm-Suggested Matches

When reviewing recommendations, you can:

- Preview each property
- See why the system thinks the user will like it
- Compare it against the user's stated preferences
- Check location, price range, and features
- Send the listing with one click

Once sent, the match appears instantly in the user's app and they can begin interacting with it right away.

## Sending Your Own Matches

You are not limited to algorithm-recommended homes.

You can also:

- Search or browse listings manually
- Add properties you think fit the user
- Send off-market or custom listings you have uploaded
- Share specific homes after a showing
- Re-send updated or similar properties

This gives you full control while still benefiting from the platform's intelligence.

## What Users See When You Send a Match

When a match is sent, the property appears in the user's app as a recommended home from you.

![Curated matches view](curated-matches)

## How Matches Drive Engagement

Sending relevant matches regularly keeps users active and moving toward decisions.

Matches allow users to:

- Swipe on homes you send
- Like or dismiss properties
- Ask questions directly from the listing
- Request showings
- Compare options more easily

For agents, this creates constant feedback loops that sharpen recommendations over time.

## Best Practices for Sending Matches

To get the most value out of matches:

- Send matches consistently, not just once
- Lean on AI recommendations for efficiency
- Review likes and dislikes after sending
- Follow up when users interact with a property
- Use matches to tee up showing conversations
- Send a short message along with important listings
- Watch for rapid engagement patterns

## How Sending Matches Fits Into Your Workflow

Matches work best when paired with:

- Claiming users
- Inviting existing clients
- Messaging regularly
- Scheduling showings
- Reviewing engagement analytics

Together, these tools give you a closed-loop system for discovery, communication, and touring.

## Next Steps

To send matches today:

1. Go to **User Management**
2. Select a user
3. Review AI recommendations
4. Send the homes most likely to resonate

If you need help sending matches or interpreting recommendations, visit the Help Center or contact support.
        `,
        tags: ['matches', 'recommendations', 'AI', 'listings', 'users'],
        readTime: 5,
        updatedAt: '2026-05-11'
      }
    ]
  },
  {
    slug: 'showings',
    title: 'Showings',
    description: 'Manage and service showing requests from users.',
    icon: 'Calendar',
    color: '#f59e0b',
    articles: [
      {
        slug: 'servicing-showings',
        title: 'Servicing Showings',
        summary: 'Manage showing requests, coordinate access, and deliver smooth property tours.',
        content: `
# Servicing Showings

Showings inside Lauft are designed to make it easy for users to raise their hand when they want to see a property and for you to manage those requests efficiently.

When a user requests a showing, that request is sent only to the agent who has claimed or is assigned to that user. No other agents will receive it.

It is important to note that showing requests in Lauft are not automatically scheduled through the MLS or the listing agent. They are simply a signal of interest. You are responsible for confirming availability, coordinating access, and officially scheduling the tour.

## How Showing Requests Work

When a user taps "Request Showing" in the app:

- The request is routed exclusively to you
- A conversation thread is created or updated
- You are notified inside the portal and by email
- The property is added to your showing requests queue

On the user side, they see clear language explaining that:

- The showing is only a request
- The time is not yet confirmed
- Their agent will contact them to finalize details

This sets expectations and prevents confusion before you confirm.

## Reviewing and Responding to Requests

To service a showing request:

1. Open the Agent Portal
2. Go to **Scheduled Showings** in the sidebar
3. Select the request
4. Review the property and requested time
5. Check availability through the listing source or listing agent
6. Confirm the time or propose alternatives
7. Message the user to finalize details

Once confirmed, you can update the showing status so both you and the user stay aligned.

## Showing Statuses

Each showing moves through clear status stages:

- **Requested** - Waiting for confirmation
- **Confirmed** - Time and access have been secured
- **Completed** - The tour has taken place
- **Cancelled** - The showing did not occur
- **Rescheduled** - A new time was agreed upon

Keeping statuses updated helps you track your pipeline and prevents missed appointments.

## Scheduling Efficient Tours

When a user requests a showing, it is often a great opportunity to line up additional properties.

Before heading out, review:

- Other homes the user has liked
- Recent swipes and saved listings
- Similar properties in the same area
- AI-recommended matches

This allows you to suggest multiple homes during the same outing and maximize the value of each tour.

## Best Practices for Servicing Showings

To deliver a smooth experience:

- Respond to requests quickly
- Confirm details at least 24 hours in advance
- Send a reminder the morning of the tour
- Arrive early whenever possible
- Follow up the same day for feedback
- Use their reactions to refine future matches
- Keep statuses updated inside the portal

## Important Reminders

- Showing requests are not MLS appointments
- You must manually coordinate access
- Requests are exclusive to the assigned agent
- Users understand that times are not confirmed until you reach out
- Lauft does not auto-book tours

## Next Steps

Head to **Scheduled Showings** in the sidebar to review new requests and manage upcoming tours.

If you have questions about servicing showings or updating statuses, visit the Help Center or contact support.
        `,
        tags: ['showings', 'scheduling', 'tours', 'requests'],
        readTime: 5,
        updatedAt: '2026-01-30'
      }
    ]
  },
  {
    slug: 'messaging',
    title: 'Messaging',
    description: 'Communicate effectively with users.',
    icon: 'MessageCircle',
    color: '#06b6d4',
    articles: [
      {
        slug: 'messaging-basics',
        title: 'Messaging Basics',
        summary: 'Keep all communication with users in one place across desktop and mobile.',
        content: `
# Messaging Basics

Messaging inside Lauft keeps all communication with users in one place.

You can message users directly from both:

- The desktop Agent Portal
- The Lauft app (where supported for agents)

Messages sync across devices so you always have a complete conversation history tied to each user.

Lauft also helps ensure you never miss an important reply by sending notifications through multiple channels.

## Start With In-App Outreach

Before calling or texting a user directly, we strongly recommend starting with a message inside the Lauft app.

In-app messaging:

- Introduces you in a professional, low-pressure way
- Matches the environment the user is already browsing in
- Keeps early communication centralized
- Creates a natural transition into phone or SMS conversations later

Users often respond faster when first contacted inside the app because it feels contextual and tied directly to the homes they are viewing.

## Have a Profile Photo Before Messaging

Your profile photo appears alongside every message you send.

Before reaching out to a user for the first time, make sure you have uploaded a professional headshot in your Agent Profile.

Agents with profile photos:

- Appear more credible
- Build trust faster
- Receive higher response rates
- Make a stronger first impression

If you have not uploaded your photo yet, head to the Dashboard and complete your profile before messaging users.

## Sending Messages

To send a message:

1. Click **Conversations** in the sidebar
2. Select the user you want to message
3. Type your message in the text box
4. Press Enter or click Send

Messages are delivered instantly to the user inside the app.

## Message Features

Messaging is more than simple text. Depending on your permissions and tools available, you can:

- Send standard text messages
- Share properties directly into the chat
- Reference previously sent matches
- Respond to showing requests
- Ask follow-up questions
- Keep all deal-related communication in one thread

This keeps context intact and avoids scattered conversations across platforms.

## Notifications

Lauft alerts you when a user responds so you can move quickly.

You may receive notifications through:

- In-portal alerts while logged in
- Email notifications to your account email
- Push notifications on mobile (if enabled)

Push notifications depend on your device settings and must be turned on to receive real-time alerts.

## Best Practices for Messaging Users

Strong communication inside Lauft helps build trust and momentum.

We recommend:

- Starting with a friendly in-app introduction
- Keeping early messages short and helpful
- Referencing a specific property or match
- Following up after sending homes
- Checking in after showings
- Staying professional and compliant
- Updating your profile photo before first contact

## Next Steps

Head to **Conversations** in the sidebar to review your active threads and respond to users.

If you have questions about messaging or notifications, visit the Help Center or contact support.
        `,
        tags: ['messaging', 'communication', 'users', 'conversations'],
        readTime: 4,
        updatedAt: '2026-01-30'
      },
      {
        slug: 'messaging-privacy-and-safety',
        title: 'Messaging Privacy and Safety',
        summary: 'Understand how messages are handled and keep your communications secure.',
        content: `
# Messaging Privacy and Safety

Messages sent inside Lauft are designed to keep communication centralized, professional, and easy to reference throughout the home search process.

To support platform integrity, troubleshooting, and dispute resolution, the Lauft team may review or access messages exchanged between agents and users when necessary.

## When the Lauft Team May Access Messages

Lauft staff may review message history in situations such as:

- Assisting with technical troubleshooting
- Reviewing user engagement and analytics
- Investigating reported issues or bugs
- Reviewing disputes between agents and users
- Responding to safety or compliance concerns
- Enforcing platform policies

This ensures that problems can be resolved fairly and efficiently while protecting both parties.

## Do Not Share Sensitive Information

Because messages may be reviewed for support or compliance reasons, agents should never send sensitive, confidential, or compromising information through in-app chat.

Avoid sharing:

- Social Security numbers
- Bank account or routing numbers
- Credit card details
- Loan application materials
- Copies of IDs or passports
- Passwords or login credentials
- Private medical or legal information

If sensitive documentation needs to be exchanged, use secure, approved methods outside of Lauft that comply with your brokerage policies.

## Keep Messages Professional and Compliant

All communication inside Lauft should remain:

- Professional
- Respectful
- Accurate
- Compliant with Fair Housing regulations
- In line with brokerage and state advertising rules

Messages inside the platform may become part of support or compliance reviews, so always communicate as if a third party may need to reference the conversation later.

## Best Practices for Safe Messaging

To protect yourself and your users:

- Keep conversations focused on properties and logistics
- Move sensitive paperwork to secure channels
- Avoid making guarantees or promises you cannot verify
- Document important decisions clearly
- Use neutral, compliant language
- Report any concerning interactions to support

## Next Steps

If you have questions about messaging privacy or need to report an issue, contact Lauft Support through the Help Center in the sidebar.
        `,
        tags: ['messaging', 'privacy', 'safety', 'compliance'],
        readTime: 4,
        updatedAt: '2026-01-30'
      }
    ]
  },
  {
    slug: 'analytics',
    title: 'Analytics & Insights',
    description: 'Understand user activity and engagement patterns.',
    icon: 'BarChart2',
    color: '#ec4899',
    articles: [
      {
        slug: 'understanding-user-activity',
        title: 'Understanding User Activity and Insights',
        summary: 'Learn how users interact with homes and use engagement signals to guide your outreach.',
        content: `
# Understanding User Activity and Insights

The Analytics section inside Lauft is designed to help you understand how users are interacting with homes, what they are responding to visually, and how their behavior is evolving over time.

Rather than focusing on traditional lead metrics, Lauft surfaces engagement signals like swipes, likes, dislikes, and match interactions so you can:

- Refine what you send
- Anticipate what users want to see next
- Spot momentum toward touring
- Guide discovery more intelligently

The more users interact with the platform, the smarter Lauft becomes at predicting what homes they will respond to.

## What Activity Data Shows You

Inside a user's profile and across your dashboard views, you can see patterns such as:

- Homes they have liked
- Properties they have dismissed
- Neighborhoods they engage with most
- Price ranges they linger on
- Layout styles they prefer
- Homes you have sent as matches
- How they reacted to those matches
- Showing requests tied to specific listings

These signals create a feedback loop between you, the user, and Lauft's recommendation engine.

## Why Swipes Matter

Every swipe helps refine what the platform shows next.

When a user likes or dislikes a home, Lauft uses that information to:

- Adjust future recommendations
- Surface similar homes
- De-prioritize styles they reject
- Improve layout and feature matching
- Sharpen neighborhood targeting

Over time, this creates a more personalized browsing experience for the user and gives you clearer insight into what is actually resonating.

## How Sending Matches Increases Signal

When you send matches to a user, you are not just sharing homes—you are actively training the system.

Each interaction with a match produces new data:

- If they like it, Lauft looks for similar homes
- If they skip it, Lauft pivots away from that style
- If they request a showing, that becomes a strong intent signal
- If they ask a question, Lauft learns what details matter most

Consistently sending matches keeps users active and accelerates how quickly Lauft zeroes in on the right homes.

## Engagement Improves Recommendations

The more time a user spends inside the app, the better Lauft performs.

High engagement leads to:

- More accurate property suggestions
- Faster alignment on style and layout
- Tighter price targeting
- Better tour planning
- Fewer wasted showings
- Stronger momentum toward decisions

Encouraging users to swipe regularly and interact with homes is one of the simplest ways to improve outcomes for everyone.

## How to Use These Insights as an Agent

You can apply user activity data to:

- Decide which homes to send next
- Identify new neighborhoods to explore
- Spot shifting priorities
- Suggest multiple homes for one outing
- Re-engage quiet users with better-matched listings
- Prepare for tours by reviewing likes and dislikes

Before heading out to show properties, reviewing recent swipes can help you curate a smarter tour route.

## Best Practices for Driving Useful Activity

To get the most value from these insights:

- Send matches consistently
- Encourage users to keep swiping
- Ask for feedback after tours
- Review patterns weekly
- Look for clusters in what they like
- Watch for sudden changes in behavior
- Use activity data to guide conversations

Agents who lean into engagement patterns tend to move faster and with more confidence when recommending homes.

## Next Steps

Visit **User Management** and open a user profile to explore their recent activity, likes, dislikes, and match history.

Use those signals to guide what you send next and how you plan upcoming tours.

If you have questions about analytics or engagement insights, check the Help Center or contact support.
        `,
        tags: ['analytics', 'insights', 'engagement', 'swipes', 'activity'],
        readTime: 5,
        updatedAt: '2026-01-30'
      }
    ]
  },
  {
    slug: 'faq',
    title: 'FAQ',
    description: 'Common questions and answers.',
    icon: 'HelpCircle',
    color: '#64748b',
    articles: [
      {
        slug: 'frequently-asked-questions',
        title: 'Frequently Asked Questions',
        summary: 'Quick answers to common questions about the Agent Portal.',
        isFaq: true,
        faqSections: [
          {
            title: 'Account & Login',
            items: [
              {
                question: 'I forgot my password.',
                answer: 'Click Forgot Password on the login page and follow the reset instructions sent to your email.'
              },
              {
                question: 'How do I change my email address?',
                answer: 'Your primary account email can be updated from your account settings or by submitting a change request through the Dashboard.'
              },
              {
                question: 'Can I have multiple agent accounts?',
                answer: 'No. Each agent should maintain only one account to ensure accurate tracking, compliance, and communication with users.'
              }
            ]
          },
          {
            title: 'Invitations & Claiming Users',
            items: [
              {
                question: 'What is the difference between inviting and claiming a user?',
                answer: 'Inviting is used to bring your own existing client into Lauft. Invited users are automatically linked to you and cost zero points. Claiming is used for marketplace users who appear in the Available Users pool and requires points based on tier.'
              },
              {
                question: 'Can I reject or ignore a user instead of claiming them?',
                answer: 'Yes. You are never required to claim a user. You can simply leave them in the Available Users pool if they are not a fit for your business.'
              },
              {
                question: 'Will other agents see users I invite?',
                answer: 'No. Users you invite are exclusive to your account and cannot be viewed or claimed by other agents.'
              }
            ]
          },
          {
            title: 'Showings',
            items: [
              {
                question: 'Can users request showings directly in the app?',
                answer: 'Yes. Users can request showings on listings, but these are only requests. You must manually confirm availability and schedule the tour.'
              },
              {
                question: 'Are showings automatically scheduled through the MLS or listing agent?',
                answer: 'No. Lauft does not book showings automatically. You are responsible for coordinating access and confirming the appointment.'
              },
              {
                question: 'How do I cancel or reschedule a showing?',
                answer: 'Open the showing inside the Agent Portal and update its status to Cancelled or Rescheduled, then message the user to confirm the change.'
              }
            ]
          },
          {
            title: 'Listings',
            items: [
              {
                question: 'Do I need to add my MLS listings manually?',
                answer: 'No. MLS listings flow into the platform automatically. You only need to add off-market or rental listings.'
              },
              {
                question: 'Are off-market and rental listings reviewed before going live?',
                answer: 'Yes. All manually added listings are reviewed by the Lauft team before appearing in the app.'
              }
            ]
          },
          {
            title: 'Billing & Points',
            items: [
              {
                question: 'How do points work?',
                answer: 'Points are used to claim marketplace users based on tier. Invited users cost zero points.'
              },
              {
                question: 'Who should I contact with billing questions?',
                answer: 'Use the Help Center to reach support for billing-related questions.'
              }
            ]
          }
        ],
        content: '',
        tags: ['FAQ', 'help', 'questions'],
        readTime: 5,
        updatedAt: '2026-01-30'
      }
    ]
  }
];

// Helper functions
export const getCategoryBySlug = (slug) => {
  return docsCategories.find(cat => cat.slug === slug);
};

export const getArticle = (categorySlug, articleSlug) => {
  const category = getCategoryBySlug(categorySlug);
  if (!category) return null;
  
  const article = category.articles.find(art => art.slug === articleSlug);
  if (!article) return null;
  
  return {
    ...article,
    categorySlug: category.slug,
    categoryTitle: category.title,
    categoryColor: category.color
  };
};

export const searchDocs = (query) => {
  const q = query.toLowerCase();
  const results = [];
  
  docsCategories.forEach(category => {
    category.articles.forEach(article => {
      const titleMatch = article.title.toLowerCase().includes(q);
      const summaryMatch = article.summary.toLowerCase().includes(q);
      const contentMatch = article.content.toLowerCase().includes(q);
      const tagMatch = article.tags.some(tag => tag.toLowerCase().includes(q));
      
      if (titleMatch || summaryMatch || contentMatch || tagMatch) {
        results.push({
          ...article,
          categorySlug: category.slug,
          categoryTitle: category.title,
          categoryColor: category.color,
          relevance: (titleMatch ? 10 : 0) + (summaryMatch ? 5 : 0) + (tagMatch ? 3 : 0) + (contentMatch ? 1 : 0)
        });
      }
    });
  });
  
  return results.sort((a, b) => b.relevance - a.relevance);
};

export const getAllArticles = () => {
  const articles = [];
  docsCategories.forEach(category => {
    category.articles.forEach(article => {
      articles.push({
        ...article,
        categorySlug: category.slug,
        categoryTitle: category.title,
        categoryColor: category.color
      });
    });
  });
  return articles;
};